import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  searchService,
  type GlobalSearchResult,
} from '../../../../services/search';
import { hasRoleLevel, RoleLevel } from '../../../../utils/roles';

type QuickCommand = {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  /** Lowest role level that sees the command (see RequireRole routes). */
  minLevel?: RoleLevel;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  roleLevel?: RoleLevel;
};

const quickCommands: QuickCommand[] = [
  {
    id: 'go-home',
    title: 'Go to Home',
    subtitle: 'Dashboard overview',
    href: '/',
  },
  {
    id: 'go-timeoff',
    title: 'Open Time Off Management',
    subtitle: 'Review and approve requests',
    href: '/time-off',
  },
  {
    id: 'go-people',
    title: 'Open People',
    subtitle: 'View employee directory',
    href: '/people',
    minLevel: 'admin',
  },
  {
    id: 'go-settings',
    title: 'Open Settings',
    subtitle: 'Workspace and security controls',
    href: '/settings',
    minLevel: 'admin',
  },
  {
    id: 'go-reports',
    title: 'Open Reports',
    subtitle: 'Analytics and exports',
    href: '/reports',
    minLevel: 'manager',
  },
];

export function CommandPalette({
  isOpen,
  onClose,
  roleLevel = 'employee',
}: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GlobalSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [isOpen]);

  useEffect(() => {
    let mounted = true;
    if (!isOpen || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        setIsSearching(true);
        const results = await searchService.globalSearch(query);
        if (mounted) {
          setSearchResults(results);
        }
      } catch (error) {
        if (mounted) {
          setSearchResults([]);
        }
      } finally {
        if (mounted) {
          setIsSearching(false);
        }
      }
    }, 200);

    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, [isOpen, query]);

  const localMatches = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const allowed = quickCommands.filter(
      (command) =>
        !command.minLevel || hasRoleLevel(roleLevel, command.minLevel),
    );
    if (!normalized) return allowed;
    return allowed.filter(
      (command) =>
        command.title.toLowerCase().includes(normalized) ||
        command.subtitle.toLowerCase().includes(normalized),
    );
  }, [query, roleLevel]);

  if (!isOpen) return null;

  const navigateTo = (href: string) => {
    onClose();
    navigate(href);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="mx-auto mt-12 w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-gray-100 px-4 py-3">
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search people, teams, workflows, or type a command..."
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-red-500"
          />
          <div className="mt-2 text-xs text-gray-500">
            Press <kbd className="rounded border px-1 py-0.5">Ctrl</kbd> +{' '}
            <kbd className="rounded border px-1 py-0.5">K</kbd> to open
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {localMatches.map((command) => (
            <button
              key={command.id}
              onClick={() => navigateTo(command.href)}
              className="mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-gray-50"
            >
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  {command.title}
                </span>
                <span className="block text-xs text-gray-500">
                  {command.subtitle}
                </span>
              </span>
              <span className="text-xs text-gray-400">Command</span>
            </button>
          ))}

          {searchResults.map((result) => (
            <button
              key={`${result.type}-${result.id}`}
              onClick={() => navigateTo(result.href)}
              className="mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-gray-50"
            >
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  {result.title}
                </span>
                <span className="block text-xs text-gray-500">
                  {result.subtitle}
                </span>
              </span>
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">
                {result.type.replace('_', ' ')}
              </span>
            </button>
          ))}

          {isSearching && (
            <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
          )}
          {!isSearching &&
            localMatches.length === 0 &&
            searchResults.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500">
                No results found.
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
